import React, { useEffect, useMemo, useState } from 'react';
import BaseChart, { useChartInstance } from '@src/components/common/BaseChart';
import { useAtomValue } from 'jotai';
import { filterCategoryTypesAtom } from '@src/atoms/page';
import metricsStore from '@globalStore/metricsStore';
import { metricKeys } from '@pulse/metrics';
import useFetchPercentiles from './hooks/useFetchPercentiles';
import { Retry } from '@src/components/common';
import { isEmpty } from 'lodash';
import { buildMultiChartOption } from '../../utils/chartOptions';
import { buildPercentileTreeOption, getLegendMap } from './utils';
import { overviewFilterColors } from '@src/utils/colorPalette';
import { ChartWrapper } from '../styled';
import WidgetCardWrapper from '../../common/WidgetCardWrapper';
import Extra from './components/Extra';
import { StackWrapper } from './styled';
import { useWidget } from '../../state/dashboard';
import WidgetWarning from '../../common/WidgetWarning';
import { getMetricUnitByKey } from '@src/utils/metrics';
import { NETWORK_BASED_CATEGORY_EXCLUDES } from '@src/utils/presets/constants';

function NetworkPercentileWidget({ wid }) {
  const widgetHandler = useWidget({ wid });
  const [showTreeMap, setShowTreeMap] = useState(false);
  const filterCategories = useAtomValue(filterCategoryTypesAtom);

  const isNAFilter = filterCategories.some((category: any) => {
    return NETWORK_BASED_CATEGORY_EXCLUDES.includes(category);
  });

  const { metricsMeta } = metricsStore.getState();
  const currentMetricsMeta = metricsMeta.app;
  const networkAvgMetric = currentMetricsMeta[metricKeys.AverageRequestDuration];

  const percentileMetrics = useMemo(() => {
    if ('percentileMetrics' in networkAvgMetric && networkAvgMetric.percentileMetrics.length > 0) {
      return [networkAvgMetric, ...networkAvgMetric.percentileMetrics];
    }
    return [];
  }, [networkAvgMetric]);

  const percentileMetricKeys = useMemo(
    () => percentileMetrics.map((m) => m.name),
    [percentileMetrics],
  );

  const getWidgets = () => {
    if (isNAFilter) {
      return <WidgetWarning message="Not Applicable" />;
    } else if (!percentileMetrics || percentileMetrics.length === 0) {
      return <WidgetWarning message="Error: No percentile metrics available" />;
    } else {
      return (
        <Percentiles wid={wid} showTreeMap={showTreeMap} percentileMetrics={percentileMetrics} />
      );
    }
  };

  return (
    <WidgetCardWrapper
      wid={wid}
      title="Network: Duration Percentiles"
      style={{ gridRow: 'span 14' }}
      extra={
        <Extra
          showTreeMap={showTreeMap}
          setShowTreeMap={setShowTreeMap}
          widgetHandler={widgetHandler}
          metrics={percentileMetricKeys}
          showOptions={!isNAFilter}
        />
      }
    >
      <StackWrapper>{getWidgets()}</StackWrapper>
    </WidgetCardWrapper>
  );
}

function Percentiles({ wid, showTreeMap, percentileMetrics }) {
  const { instance, chartInstanceRefCb } = useChartInstance({});

  const {
    data = { data: [], totals: {} },
    isFetching,
    isError,
    refetch,
    queued,
  } = useFetchPercentiles({ wid, metrics: percentileMetrics.map((m) => m.name) });

  const chartOption = useMemo(() => {
    const legendMap = getLegendMap(percentileMetrics);
    const colorMap = {};
    percentileMetrics.forEach((metric, idx) => {
      colorMap[metric.name] = overviewFilterColors[idx];
    });

    if (showTreeMap) {
      const treeData: any = [];
      percentileMetrics.forEach((m) => {
        treeData.push({ name: m.name, value: data.totals[m.name] });
      });
      return buildPercentileTreeOption({
        treeData,
        seriesName: 'network-percentile',
        colorMap,
        legendMap,
        unit: getMetricUnitByKey({ metricKey: percentileMetrics[0].name }),
      });
    }
    return buildMultiChartOption({
      data: data.data,
      metrics: percentileMetrics.map((m) => m.name),
      colorMap,
      legendMap,
    });
  }, [percentileMetrics, data, showTreeMap]);

  useEffect(() => {
    if (instance) {
      instance.setOption(chartOption, { notMerge: true });
    }
  }, [instance, chartOption]);

  return (
    <ChartWrapper>
      <Retry
        isEmpty={isEmpty(data)}
        isLoading={isFetching}
        showError={isError}
        fetchData={refetch}
        queued={queued}
      >
        <BaseChart chartInstanceRefCb={chartInstanceRefCb} style={{ height: 210, width: '100%' }} />
      </Retry>
    </ChartWrapper>
  );
}

export default NetworkPercentileWidget;
